// Wahl des Feldes für einen neuen Filter (Agent 5A, Runde 5): Suchfeld, Kategorien mit Feldern, darunter „Hat …, bei der“ für
// verwandte Objekte. Ein Klick (oder Enter) wählt; die Pfeiltasten gehen durch die Liste. Schlanker als das 500-px-Panel von Close:
// ein Popover, das sich beim Tippen auf die Treffer kürzt.
import { useEffect, useMemo, useRef, useState } from "react";
import type { FilterFeldArt, FilterKatalogFeld, FilterKategorie } from "../../api/filter";
import { FILTER_TEXTE } from "./filterTexte";

const ART_ZEICHEN: Record<FilterFeldArt, string> = {
  status: "≡",
  nutzer: "◉",
  wahl: "≡",
  enum: "≡",
  zeit: "◷",
  datum: "▦",
  zahl: "#",
  dauer: "◷",
  text: "T",
  volltext: "T",
};

const normal = (s: string) => s.toLocaleLowerCase("de").normalize("NFD").replace(/[̀-ͯ]/g, "");

interface Zeile {
  schluessel: string;
  kategorie: string | null;
  feld?: FilterKatalogFeld;
  verwandt?: { id: string; label: string };
}

interface Props {
  kategorien: FilterKategorie[];
  /** verwandte Objekte zum Anhängen („Hat eine Opportunity, bei der …“); fehlt beim Innern eines verwandten Objekts */
  verwandt?: { id: string; label: string }[];
  onFeld: (feld: FilterKatalogFeld) => void;
  onVerwandt?: (id: string) => void;
}

export function FilterFeldWahl({ kategorien, verwandt = [], onFeld, onVerwandt }: Props) {
  const [suche, setSuche] = useState("");
  const [aktiv, setAktiv] = useState(0);
  const eingabe = useRef<HTMLInputElement>(null);
  const liste = useRef<HTMLDivElement>(null);

  const zeilen = useMemo<Zeile[]>(() => {
    const n = normal(suche.trim());
    const aus: Zeile[] = [];
    for (const k of kategorien) {
      const treffer = k.felder.filter((f) => !n || normal(f.label).includes(n) || (n.length > 2 && normal(k.label).includes(n)));
      treffer.forEach((f, i) => aus.push({ schluessel: `${k.id}:${f.id}`, kategorie: i === 0 ? k.label : null, feld: f }));
    }
    const v = verwandt.filter((x) => !n || normal(`${FILTER_TEXTE.hatVerwandt(x.label)}`).includes(n));
    v.forEach((x, i) => aus.push({ schluessel: `v:${x.id}`, kategorie: i === 0 ? FILTER_TEXTE.verwandteObjekte : null, verwandt: x }));
    return aus;
  }, [kategorien, verwandt, suche]);

  useEffect(() => setAktiv(0), [suche]);
  useEffect(() => {
    eingabe.current?.focus();
  }, []);
  useEffect(() => {
    liste.current?.querySelector<HTMLElement>(`[data-index="${aktiv}"]`)?.scrollIntoView({ block: "nearest" });
  }, [aktiv]);

  const waehlen = (z: Zeile | undefined) => {
    if (!z) return;
    if (z.feld) {
      if (!z.feld.deaktiviert) onFeld(z.feld);
    } else if (z.verwandt) onVerwandt?.(z.verwandt.id);
  };

  const taste = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setAktiv((a) => Math.min(a + 1, zeilen.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setAktiv((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      waehlen(zeilen[aktiv]);
    }
  };

  return (
    <div className="fw" onKeyDown={taste}>
      <div className="fw__suche">
        <input ref={eingabe} className="eingabe" value={suche} placeholder={FILTER_TEXTE.feldSuchen} aria-label={FILTER_TEXTE.feldSuchen} onChange={(e) => setSuche(e.target.value)} />
      </div>
      <div className="fw__liste" ref={liste} role="listbox" aria-label={FILTER_TEXTE.feldWaehlen}>
        {zeilen.length === 0 ? <div className="fw__leer">{FILTER_TEXTE.keinFeld}</div> : null}
        {zeilen.map((z, i) => (
          <div key={z.schluessel}>
            {z.kategorie ? <div className="fw__kategorie">{z.kategorie}</div> : null}
            <button
              type="button"
              role="option"
              aria-selected={i === aktiv}
              data-index={i}
              className={`fw__feld${i === aktiv ? " fw__feld--aktiv" : ""}`}
              disabled={!!z.feld?.deaktiviert}
              title={z.feld?.deaktiviert ?? z.feld?.hinweis}
              onMouseEnter={() => setAktiv(i)}
              onClick={() => waehlen(z)}
            >
              <span className="fw__art" aria-hidden="true">
                {z.feld ? ART_ZEICHEN[z.feld.art] : "⇄"}
              </span>
              <span className="fw__name">{z.feld ? z.feld.label : FILTER_TEXTE.hatVerwandt(z.verwandt?.label ?? "")}</span>
              {z.feld?.deaktiviert ? <span className="fw__grund">{FILTER_TEXTE.nichtMoeglich}</span> : null}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
